import { ReactNode } from "react";
import { useNavigate } from "react-router";

import FeedbackButton from "components/FeedbackButton";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { Gear, Plus } from "components/icons/generated";
import Box, { BoxProps } from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import Icon from "ds/components/Icon";
import Tab from "ds/components/Tab";
import TextEllipsis from "ds/components/TextEllipsis";
import Tooltip from "ds/components/Tooltip";
import Typography from "ds/components/Typography";
import useTypedContext from "hooks/useTypedContext";

import { DASHBOARD_HEADER_PENDO_FEEDBACK_ID, NEW_TAB_IN_LIST_ID } from "./constants";
import { DashboardContext } from "./context";
import styles from "./styles.module.css";
import useCurrentTab from "./useCurrentTab";

type DashboardViewHeaderProps = Omit<BoxProps, "children"> & { children?: ReactNode };

function DashboardViewHeader({ children, ...rest }: DashboardViewHeaderProps) {
  const navigate = useNavigate();
  const { canAddMoreViews, openManageDrawer, addNewTab, configLoading, tabs } =
    useTypedContext(DashboardContext);

  const currentTab = useCurrentTab();

  const handleTabClick = (id: string) => {
    navigate(`/dashboard?tab=${id}`);
  };

  const addNewTabHandler = () => {
    if (canAddMoreViews) {
      addNewTab();
    }
  };

  return (
    <ViewHeader firstLevel>
      <Box align="start" justify="between" fullWidth>
        <Box gap="medium" direction="column" grow="1" fullWidth>
          <ViewHeaderWrapper justify="between" align="center">
            <ViewHeaderTitle>Dashboard</ViewHeaderTitle>
            <Box gap="medium">
              <FeedbackButton id={DASHBOARD_HEADER_PENDO_FEEDBACK_ID} />
              <ButtonIcon
                variant="secondary"
                icon={Gear}
                disabled={configLoading}
                size="medium"
                onPress={() => openManageDrawer(undefined, { method: "Header button click" })}
              >
                Manage view
              </ButtonIcon>
            </Box>
          </ViewHeaderWrapper>
          <Box className={styles.tabsContainerScollable}>
            <Box gap="medium" className={styles.tabsContainer}>
              {tabs.map(({ id, title }) => (
                <Tab
                  key={id}
                  isActive={currentTab === id}
                  onClick={handleTabClick}
                  id={id}
                  aria-label={title}
                  label={
                    <TextEllipsis tooltip={title} tooltipWidthMode="maxWidthSm">
                      {(props) => (
                        <Typography {...props} tag="span" variant="p-t7">
                          {title}
                        </Typography>
                      )}
                    </TextEllipsis>
                  }
                />
              ))}
              <Tab
                id={NEW_TAB_IN_LIST_ID}
                onClick={addNewTabHandler}
                aria-label={canAddMoreViews ? "Add new tab" : "Views limit exceeded"}
                isActive={false}
                label={
                  <Tooltip on={(props) => <Icon {...props} src={Plus} />}>
                    {canAddMoreViews ? "Add new tab" : "Views limit exceeded"}
                  </Tooltip>
                }
                disabled={!canAddMoreViews}
              />
            </Box>
          </Box>
        </Box>
        <Box {...rest}>{children}</Box>
      </Box>
    </ViewHeader>
  );
}

export default DashboardViewHeader;
